import { IVL_INVENTORY_EXPORT_LIMIT } from '@/services/inventory/inventoryStatus/ivlConstants'
/** FilterArea 필드 id — 그대로 폼 값 키가 된다. */
export const FIELD_NAMES = {
  logisticsServiceTypes: 'logisticsServiceTypes',
  shipperNames: 'shipperNames',
  whNm: 'whNm',
  brandNames: 'brandNames',
  gdsCd: 'gdsCd',
  barcodes: 'barcodes',
  skuIds: 'skuIds',
  styleNumbers: 'styleNumbers',
} as const

/**
 * pbo-core `FilterArea` 의 `allOption` 이 끼워 넣는 '전체' 항목 값 — **빈 문자열**이다.
 * (독립 `FormField` 는 `__ALL__` 을 쓰지만 FilterArea 내부 셀렉트는 `''` 다.)
 *
 * 서버 계약도 같다 — 빈 값이면 센터 조건을 걸지 않는다(1.0 ST02Container ALL→'' 동치).
 * 그래서 '미선택'(undefined)과 '전체'(`''`)를 구분해 필수 검증해야 한다.
 */
export const WAREHOUSE_ALL = ''

/** 화주유형 기본 선택값 — 1.0 ST02 필터 기본값과 동일. */
export const DEFAULT_LOGISTICS_SERVICE_TYPE = 'M1P'

/** 화주명 '전체' 값. 센터명과 같이 빈 문자열이면 서버가 조건을 걸지 않는다. */
export const SHIPPER_NAME_ALL = ''

/**
 * 재고현황 상품정보 원천 FF — `feature.skuIdTransition` 오브젝트의 `st02View` 필드 (LOGISPRD-14186).
 * BE `SkuIdTransitionFlag.ST02_VIEW` 와 같은 키다. 값이 [ST02_VIEW_SKU_ATTRIBUTE] 일 때만 BE 가 `sku_attribute` 를 읽는다.
 */
export const SKU_ID_TRANSITION_FLAG_KEY = 'feature.skuIdTransition'
export const ST02_VIEW_FIELD = 'st02View'
export const ST02_VIEW_SKU_ATTRIBUTE = 'SKU_ATTRIBUTE'

/**
 * `st02View` 가 [ST02_VIEW_SKU_ATTRIBUTE] 일 때 브랜드명·스타일넘버 조회조건을 남기는 화주유형.
 * 그 밖의 유형은 두 조건을 잠그고 값을 비운다 (SPEC-inventory-st02-sku-attribute-join-A AC-09·AC-10).
 */
export const PRODUCT_FILTER_SHIPPER_TYPES: readonly string[] = ['MWP', 'MLT']

/** 그리드 기본 페이지 크기. */
export const DEFAULT_PAGE_SIZE = 50

/**
 * 그리드 고정 높이(px). ag-grid 래퍼가 `flex: 1`(flex-basis 0)이라 부모 높이가 auto 면 0 으로 접힌다 —
 * 페이지 컨테이너가 콘텐츠 높이를 따르므로 명시적 높이가 필요하다.
 */
export const GRID_HEIGHT_PX = 600

/** 큰 페이지는 현재 보이는 행만 그려 표의 DOM 크기를 제한한다. */
export const INVENTORY_TABLE_VIRTUAL_THRESHOLD = 100

/** 필수 표시(*) 색. antd 기본 에러색이며 필터·다운로드 사유 모달이 함께 쓴다. */
export const REQUIRED_MARK_COLOR = '#ff4d4f'

/**
 * 엑셀 다운로드 행수 상한. 초과 시 조회조건 추가를 안내하고 요청하지 않는다.
 * 1.0 ToolBar.onBtnExport 와 같은 5만 기준이다(BE 는 10만에서 400 을 주므로 FE 가 먼저 걸린다).
 */
export const EXCEL_ROW_LIMIT = 50_000

/** 엑셀 사유 모달의 상세사유 최소 길이 — 1.0 RemarkPop 검증과 동일(옛 ExcelDownloadReasonModal 에서 이관). */
export const REASON_DETAIL_MIN_LENGTH = 5

/**
 * 상세사유 최대 길이. BE 는 `{사유코드}_{상세사유}` 로 이어 감사로그 remark(varchar 1000)에 넣고,
 * 엑셀은 GET 이라 사유가 쿼리스트링에 실린다(한글은 URL 인코딩 시 3배) — 옛 모달의 maxLength 와 같은 값.
 * 뷰의 textarea 에는 maxLength 가 없으므로 배선이 대신 막는다.
 */
export const REASON_DETAIL_MAX_LENGTH = 200

export const MESSAGES = {
  /** IVL(새 재고 조회) — 조건이 하나도 없으면 조회하지 않는다. */
  ivlConditionRequired: '조회 조건을 하나 이상 입력해 주세요.',
  /** IVL 엑셀 — 조회 결과가 없거나 조회 중이면 내려받지 않는다. */
  ivlSearchFirst: '재고를 조회한 뒤 다운로드해 주세요.',
  /** IVL 엑셀 — 조회·다운로드가 도는 동안은 대기(실패=알럿). */
  ivlBusy: '조회 또는 다운로드가 진행 중이에요. 잠시 기다려 주세요.',
  /** IVL 엑셀 상한 — 숫자는 IVL_INVENTORY_EXPORT_LIMIT 를 따른다. */
  ivlExportLimitExceeded: `조회 대상이 ${IVL_INVENTORY_EXPORT_LIMIT.toLocaleString()}건 이상이에요. 조회 조건을 좁혀 주세요.`,
  ivlExportCountUnknown: '전체 건수를 확인하지 못해 엑셀을 다운로드할 수 없어요. 조회 조건을 좁혀 주세요.',
  /** IVL 서버 페이지 — 페이지 번호·크기가 범위를 벗어났을 때. */
  ivlPageInvalid: '페이지와 조회 건수를 확인해 주세요.',
  shipperRequired: '화주유형, 화주명 중 하나는 필수입니다.',
  warehouseRequired: '조회 할 센터를 선택해 주세요.',
  ivlAllWarehouseConditionRequired: '브랜드, SKU, 바코드, 상품번호, 스타일넘버 중 하나 이상을 입력해 주세요.',
  /**
   * 새 디자인의 센터명은 멀티셀렉트인데 조회 API `whNm` 은 단건 EQUAL 이다.
   * BE 가 다건(IN)을 받게 되면 이 문구와 index.tsx 의 선차단을 함께 걷어낸다.
   */
  warehouseSingleOnly: '센터명은 한 곳만 선택할 수 있습니다. 여러 센터를 함께 보려면 전체를 선택해 주세요.',
  excelReasonRequired: '사유코드를 선택해 주세요.',
  excelReasonDetailTooShort: `상세사유를 ${REASON_DETAIL_MIN_LENGTH}글자 이상 입력해 주세요.`,
  excelReasonDetailTooLong: `상세사유는 ${REASON_DETAIL_MAX_LENGTH}글자를 넘을 수 없습니다.`,
  /** 화주유형을 바꾸면 화주명 목록이 통째로 갈린다 — 이전 유형의 화주가 남은 채 조회되지 않게 막는다. */
  shipperNameNotInType: '선택한 화주명이 화주유형과 맞지 않습니다. 화주명을 다시 선택해 주세요.',
  /** 유형을 바꾼 직후 — 새 화주명 목록이 오기 전에는 유형↔화주명 일치를 판정할 수 없다. */
  shipperNamesLoading: '화주명 목록을 불러오는 중입니다. 잠시 후 다시 조회해 주세요.',
  excelRowLimitExceeded: `데이터가 ${EXCEL_ROW_LIMIT.toLocaleString()}건이 초과됩니다. 조회 조건을 추가해주세요`,
  excelNoRows: '다운로드할 데이터가 없습니다.',
  /** 상품코드 서치헬퍼 — goods-codes 는 무페이징 distinct 라 조건 없이 받지 않는다(index.tsx enabled). */
  goodsHelperConditionRequired: '브랜드명 또는 상품명을 입력한 뒤 조회해 주세요.',
} as const
